import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import UserHoverCard from '@/shared/UserHoverCard'
import classNames from '@/utils/classNamesLocal'
import Section from '@/pages/Users/PublicProfile/Section'

const PAGE = 20

const Row = ({ recommendation }) => {
  const history = useHistory()
  const handleContentClick = (e) => {
    if (!e.target.closest('a')) { return }
    if (e.target.getAttribute('data-mention') === 'mention') { e.preventDefault(); history.push('/' + e.target.getAttribute('data-to')) }
  }
  return <li className='flex gap-x-3'>
    <img className='h-10 w-10 flex-shrink-0 rounded-lg object-cover' src={recommendation.imageUrl} alt='' />
    <div className='min-w-0 flex-1'>
      <div className='flex flex-wrap items-center gap-x-2 text-sm'>
        <UserHoverCard username={recommendation.username} displayName={recommendation.displayName} />
        <time className='text-xs uppercase text-gray-500 dark:text-gray-400'>{recommendation.createdAt}</time>
      </div>
      <p onClick={handleContentClick} className='mt-0.5 text-sm text-gray-800 dark:text-gray-200' dangerouslySetInnerHTML={{ __html: recommendation.body }} />
    </div>
  </li>
}

// Received / Given, pick one. The first five received ride in with the
// profile; everything else pages in twenty at a time.
const PublicRecommendations = () => {
  const [profileContainer] = useGlobalState('profileContainer')
  const { getRequest } = useQuery()
  const { user, recommendationsReceived = [], recommendationsReceivedCount = 0, recommendationsGivenCount = 0 } = profileContainer
  const [tab, setTab] = useState('received')
  const [lists, setLists] = useState({ received: recommendationsReceived, given: [] })
  const [pages, setPages] = useState({ received: 0, given: 0 }) // pages fetched so far
  const [loading, setLoading] = useState(false)

  const counts = { received: recommendationsReceivedCount, given: recommendationsGivenCount }
  if (counts.received === 0 && counts.given === 0) { return null }

  const loadMore = (which) => {
    const nextPage = pages[which] + 1
    setLoading(true)
    getRequest(`/api/v3/users/${user.id}/recommendations`, { page: nextPage }, (err, jsonData) => {
      setLoading(false)
      if (err) { return }
      const incoming = which === 'received' ? jsonData.recommendationsReceived : jsonData.recommendationsGiven
      setLists((l) => {
        const seen = new Set(nextPage === 1 ? [] : l[which].map((r) => r.id))
        const base = nextPage === 1 ? [] : l[which]
        return { ...l, [which]: [...base, ...(incoming || []).filter((r) => !seen.has(r.id))] }
      })
      setPages((p) => ({ ...p, [which]: nextPage }))
    })
  }

  const switchTo = (which) => {
    setTab(which)
    if (which === 'given' && pages.given === 0) { loadMore('given') }
  }

  const list = lists[tab]
  const hasMore = list.length < counts[tab]

  const toggle = <div role='group' aria-label='Received or given' className='inline-flex rounded-full bg-gray-100 p-0.5 dark:bg-gray-700'>
    { [['received', 'Received'], ['given', 'Given']].map(([key, label]) => (
      <button key={key} type='button' onClick={() => switchTo(key)} aria-pressed={tab === key}
        className={classNames('rounded-full px-3 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cccblue',
          tab === key ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white')}>
        {label} <span className='text-gray-400'>{counts[key]}</span>
      </button>
    ))}
  </div>

  return <Section title='Recommendations' aside={toggle}>
    <ul className='space-y-4'>{ list.map((r) => <Row key={`rec${r.id}`} recommendation={r} />) }</ul>
    { !loading && list.length === 0 && <p className='text-sm text-gray-500 dark:text-gray-400'>{ tab === 'given' ? `${user.displayName} hasn't written any yet.` : 'None yet.' }</p> }
    { loading && <Loading /> }
    { !loading && hasMore && <button type='button' onClick={() => loadMore(tab)} className='mt-4 text-sm font-semibold text-cccblue hover:underline'>Show more</button> }
  </Section>
}

export default PublicRecommendations

Row.propTypes = { recommendation: PropTypes.object.isRequired }
